// Tradução amigável dos erros de template retornados pela API Oficial do WhatsApp
// (coluna chats.motivo_template_wp). A API devolve textos técnicos em inglês; aqui
// convertemos para um título curto + explicação + causa provável + o que fazer.
//
// Usado em /chat (balão de falha) e /templates-com-erro (faixa de erro + filtro),
// para manter a tradução consistente nos dois lugares. O casamento é por trecho do
// texto (case-insensitive) para resistir a pequenas variações da mensagem do Meta.

export type CategoriaErroTemplate =
  | "marketing"
  | "experimento"
  | "optout"
  | "janela"
  | "indisponivel"
  | "limite"
  | "template"
  | "desconhecido";

export interface ErroTemplateInfo {
  /** Título curto e amigável (PT-BR). */
  titulo: string;
  /** Explicação em uma frase do que aconteceu. */
  resumo: string;
  /** Causa provável (mais detalhada) — exibida no botão de info. */
  causa: string;
  /** Sugestão de "o que fazer" — opcional. */
  acao?: string;
  /** Categoria, usada para ícone/cor e agrupamentos. */
  categoria: CategoriaErroTemplate;
}

// Ícone (Tabler) por categoria — usado pela UI.
export const ICONE_CATEGORIA: Record<CategoriaErroTemplate, string> = {
  marketing: "ti ti-speakerphone",
  experimento: "ti ti-flask",
  optout: "ti ti-hand-stop",
  janela: "ti ti-clock-x",
  indisponivel: "ti ti-phone-off",
  limite: "ti ti-gauge",
  template: "ti ti-file-alert",
  desconhecido: "ti ti-alert-circle",
};

const inclui = (txt: string, ...termos: string[]) =>
  termos.some((t) => txt.includes(t));

/**
 * Traduz o motivo cru (motivo_template_wp) para uma versão amigável.
 * Sempre retorna um objeto — cai num fallback genérico quando não reconhece o texto.
 */
export function traduzirErroTemplate(motivoCru?: string | null): ErroTemplateInfo {
  const txt = (motivoCru || "").trim().toLowerCase();

  if (!txt) {
    return {
      titulo: "Falha no envio do template",
      resumo: "O WhatsApp não entregou esta mensagem e não informou o motivo.",
      causa: "A API Oficial do WhatsApp não retornou um motivo para esta falha.",
      categoria: "desconhecido",
    };
  }

  // 1) Limite de marketing por destinatário (Meta error 131049)
  if (inclui(txt, "healthy ecosystem")) {
    return {
      titulo: "Limite de marketing do WhatsApp",
      resumo: "O WhatsApp não entregou esta mensagem promocional a este contato.",
      causa:
        "O Meta limita quantas mensagens de marketing cada pessoa recebe por período " +
        "(somando todas as empresas). Quando o contato já recebeu muitas mensagens " +
        "promocionais ou costuma não responder, o WhatsApp deixa de entregar novas " +
        "mensagens de marketing. Não é punição ao seu número — sua qualidade continua normal.",
      acao:
        "Prefira templates da categoria UTILITY (avisos, lembretes, confirmações) para esse " +
        "tipo de conteúdo, ou responda dentro da janela de 24h. Concentre o marketing em " +
        "contatos que costumam interagir.",
      categoria: "marketing",
    };
  }

  // 2) Destinatário no experimento de limite de marketing do Meta
  if (inclui(txt, "part of an experiment", "experiment")) {
    return {
      titulo: "Contato em experimento do Meta",
      resumo:
        "O WhatsApp não entregou porque este contato está num grupo de teste do Meta que " +
        "restringe mensagens de marketing.",
      causa:
        "O Meta seleciona parte dos usuários para limitar as mensagens de marketing que " +
        "recebem. Esse contato está nesse grupo no momento — o bloqueio é do lado do " +
        "destinatário, não do seu número.",
      acao:
        "Use template UTILITY quando o conteúdo for transacional, ou aguarde o contato " +
        "responder e fale dentro da janela de 24h.",
      categoria: "experimento",
    };
  }

  // 3) Contato optou por sair do marketing
  if (inclui(txt, "stop receiving marketing", "marketing messages")) {
    return {
      titulo: "Contato saiu do marketing",
      resumo:
        "Este contato pediu para parar de receber mensagens de marketing da sua empresa no WhatsApp.",
      causa:
        "O contato tocou em 'Parar promoções' (opt-out). A partir daí o WhatsApp bloqueia " +
        "mensagens de marketing para ele.",
      acao:
        "Não envie mais marketing para este contato. Mensagens UTILITY (transacionais) ainda " +
        "podem ser entregues quando aplicável.",
      categoria: "optout",
    };
  }

  // 4) Janela de 24h expirada / re-engajamento
  if (inclui(txt, "24 hours", "re-engagement", "reengagement")) {
    return {
      titulo: "Janela de 24h expirada",
      resumo:
        "Passaram-se mais de 24h desde a última resposta do contato, então mensagens livres " +
        "não são mais permitidas.",
      causa:
        "Fora da janela de 24h, o WhatsApp só permite o envio de templates aprovados. Esta " +
        "mensagem não foi entregue porque a janela já havia fechado.",
      acao:
        "Envie um template aprovado para reabrir a conversa, ou aguarde o contato responder novamente.",
      categoria: "janela",
    };
  }

  // 5) Número sem WhatsApp / não registrado
  if (inclui(txt, "not a valid whatsapp", "not registered", "no whatsapp", "not on whatsapp")) {
    return {
      titulo: "Número sem WhatsApp",
      resumo: "Este número não está registrado no WhatsApp.",
      causa: "O número informado não possui uma conta de WhatsApp ativa.",
      acao: "Verifique se o número do contato está correto.",
      categoria: "indisponivel",
    };
  }

  // 6) Limite de volume / velocidade de envio
  if (inclui(txt, "rate limit", "too many messages", "rate-limit")) {
    return {
      titulo: "Muitos envios em pouco tempo",
      resumo: "O WhatsApp limitou o envio por volume alto num curto período.",
      causa: "Foi atingido o limite de velocidade/quantidade de mensagens do número.",
      acao: "Reduza o ritmo de envios e tente novamente mais tarde.",
      categoria: "limite",
    };
  }

  // 7) Template pausado / desativado / inexistente
  if (
    inclui(txt, "template") &&
    inclui(txt, "paused", "disabled", "does not exist", "not exist", "deleted")
  ) {
    return {
      titulo: "Template indisponível",
      resumo: "O template usado está pausado, desativado ou não existe mais.",
      causa:
        "Templates com baixa qualidade podem ser pausados pelo Meta, ou o template foi " +
        "removido/renomeado no gerenciador do WhatsApp.",
      acao: "Revise o template no gerenciador do WhatsApp ou use outro template aprovado.",
      categoria: "template",
    };
  }

  // 8) Indisponível (genérico)
  if (inclui(txt, "undeliverable", "unable to deliver")) {
    return {
      titulo: "Mensagem não pôde ser entregue",
      resumo: "O WhatsApp não conseguiu entregar esta mensagem ao contato.",
      causa:
        "Geralmente o número não tem WhatsApp ativo, foi digitado errado ou está " +
        "temporariamente indisponível.",
      acao: "Confira se o número está correto e tem WhatsApp. Se persistir, corrija o contato.",
      categoria: "indisponivel",
    };
  }

  // Fallback: motivo não reconhecido — mostra texto original na UI.
  return {
    titulo: "Falha no envio do template",
    resumo: "O WhatsApp não entregou esta mensagem.",
    causa: "O motivo exato foi retornado pelo WhatsApp (veja a mensagem original abaixo).",
    categoria: "desconhecido",
  };
}
